import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  Chip,
  Grid,
  Paper
} from '@mui/material';
import { FiX, FiEdit2, FiDownload } from 'react-icons/fi';
import { format } from 'date-fns';

interface TransactionDetailsDrawerProps {
  open: boolean;
  transaction: any;
  onClose: () => void;
  onEdit: () => void;
}

export const TransactionDetailsDrawer: React.FC<TransactionDetailsDrawerProps> = ({
  open,
  transaction,
  onClose,
  onEdit
}) => {
  if (!transaction) return null;

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 600, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Transaction Details</Typography>
          <IconButton onClick={onClose}>
            <FiX />
          </IconButton>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Date</Typography>
                  <Typography>
                    {format(new Date(transaction.date), 'MMM dd, yyyy')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip 
                    label={transaction.status} 
                    color={transaction.status === 'approved' ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Description</Typography>
                  <Typography>{transaction.description}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>Line Items</Typography>
            <Paper>
              <List>
                {transaction.entries?.map((entry: any, index: number) => (
                  <ListItem key={index} divider={index !== transaction.entries.length - 1}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2">Account</Typography>
                        <Typography>{entry.accountName}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="subtitle2">Debit</Typography>
                        <Typography>{entry.debit ? formatAmount(entry.debit) : '-'}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="subtitle2">Credit</Typography>
                        <Typography>{entry.credit ? formatAmount(entry.credit) : '-'}</Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="subtitle2">Fund</Typography>
                        <Typography>{entry.fundName || '-'}</Typography>
                      </Grid>
                    </Grid>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                startIcon={<FiEdit2 />}
                onClick={onEdit}
                fullWidth
              >
                Edit Transaction
              </Button>
              <Button
                variant="outlined"
                startIcon={<FiDownload />}
                fullWidth
              >
                Export PDF
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
}; 